import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import dayjs from 'dayjs';
import { ChevronDown, Plus, Trash2 } from 'lucide-react';
import useSWR from 'swr';

import { Button } from 'components/Button';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { useToast } from 'hooks/useToaster';
import {
  GlobalTranzNoteType,
  deleteGlobalTranzNote,
  getGlobalTranzNoteTypes,
  submitGlobalTranzNote,
} from 'lib/api/globalTranzNotes';
import { GLOBALTRANZ_STATUS } from 'pages/QuoteView/LoadBuilding/GlobalTranzSectionForms/constants';
import { Load, Note } from 'types/Load';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { cn } from 'utils/shadcn';

import { AddNoteDialog } from './AddNoteDialog';
import { DeleteNoteDialog } from './DeleteNoteDialog';

type NotesSectionFormProps = {
  load: Pick<
    Load,
    'ID' | 'serviceID' | 'freightTrackingID' | 'notes' | 'status'
  >;
  tmsID: number;
  onNotesChanged?: (notes: Note[]) => void | Promise<void>;
};

const GLOBAL_TRANZ_TL_QUOTE_MODE = 3;

function NoteDescription({ text = '' }: { text?: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLElement>(null);
  const measurementRef = useRef<HTMLElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);

  const measureOverflow = useCallback(() => {
    const description = descriptionRef.current;
    const measurement = measurementRef.current;
    if (!description || !measurement) {
      return;
    }

    setCanExpand(measurement.scrollHeight > description.clientHeight + 1);
  }, []);

  useLayoutEffect(() => {
    if (isExpanded) {
      return;
    }

    measureOverflow();
    const container = containerRef.current;
    const resizeObserver =
      container && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(measureOverflow)
        : undefined;

    if (container && resizeObserver) {
      resizeObserver.observe(container);
    }

    window.addEventListener('resize', measureOverflow);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', measureOverflow);
    };
  }, [isExpanded, measureOverflow, text]);

  return (
    <Flex
      ref={containerRef}
      direction='col'
      align='start'
      className='relative w-full min-w-0'
    >
      <Typography
        ref={descriptionRef}
        className={cn(
          'w-full min-w-0 max-w-full text-[13px] text-input-text leading-relaxed whitespace-pre-wrap break-words [overflow-wrap:anywhere]',
          !isExpanded && 'line-clamp-2'
        )}
      >
        {text}
      </Typography>
      <Typography
        ref={measurementRef}
        aria-hidden='true'
        className='pointer-events-none invisible absolute inset-x-0 top-0 w-full min-w-0 max-w-full text-[13px] leading-relaxed whitespace-pre-wrap break-words [overflow-wrap:anywhere]'
      >
        {text}
      </Typography>

      {canExpand && (
        <div className='mt-2 w-full border-t border-neutral-200 pt-2 text-center'>
          <Button
            buttonNamePosthog={ButtonNamePosthog.ToggleExpandableContent}
            type='button'
            variant='unstyled'
            size='auto'
            aria-expanded={isExpanded}
            className='inline-flex cursor-pointer items-center gap-1 text-xs text-brand-600 hover:text-brand-700'
            onClick={() => setIsExpanded((expanded) => !expanded)}
          >
            <ChevronDown
              className={cn(
                'h-3.5 w-3.5 transition-transform',
                isExpanded && 'rotate-180'
              )}
            />
            {isExpanded ? 'Show less' : 'Show more'}
          </Button>
        </div>
      )}
    </Flex>
  );
}

export function NotesSectionForm({
  load,
  tmsID,
  onNotesChanged,
}: NotesSectionFormProps) {
  const { toast } = useToast();
  const isCancelled = load.status === GLOBALTRANZ_STATUS.Canceled;
  const [history, setHistory] = useState<Note[]>(load.notes ?? []);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [deletingNoteID, setDeletingNoteID] = useState<number>();
  const [pendingDeleteNoteID, setPendingDeleteNoteID] = useState<number>();

  const {
    data: noteTypes,
    error: noteTypesError,
    isLoading: noteTypesAreLoading,
    mutate: revalidateNoteTypes,
  } = useSWR<GlobalTranzNoteType[]>(
    tmsID
      ? ['globaltranz-note-types', tmsID, GLOBAL_TRANZ_TL_QUOTE_MODE]
      : null,
    async () => {
      const result = await getGlobalTranzNoteTypes(
        tmsID,
        GLOBAL_TRANZ_TL_QUOTE_MODE
      );
      if (result.isErr()) {
        throw new Error(result.error.message);
      }

      return result.value;
    },
    { revalidateOnFocus: false }
  );

  const filteredNotes = useMemo(() => {
    return history.filter((note) => !note.isDeleted).reverse();
  }, [history]);

  const handleAddDialogOpenChange = (open: boolean) => {
    setIsAddDialogOpen(open);

    if (open && noteTypesError) {
      void revalidateNoteTypes(undefined, {
        revalidate: true,
        throwOnError: false,
      });
    }
  };

  const noteTypeLabels = useMemo(
    () =>
      new Map(
        (noteTypes ?? []).map((noteType) => [
          noteType.id,
          noteType.description || noteType.value,
        ])
      ),
    [noteTypes]
  );

  const handleAddNote = async (
    noteTypeID: number,
    noteText: string
  ): Promise<boolean> => {
    if (!load.ID) {
      return false;
    }

    setIsAdding(true);
    try {
      const result = await submitGlobalTranzNote(load.ID, noteTypeID, noteText);

      if (result.isOk()) {
        setHistory(result.value);
        await onNotesChanged?.(result.value);
        toast({
          description: 'Successfully added note.',
          variant: 'success',
        });
        return true;
      }

      if (result.error.level === 'warning') {
        // GTZ committed the add but refresh failed. Seed the form with the
        // current known history so a later save does not revert to load.notes.
        await onNotesChanged?.(history);
        toast({
          description: result.error.message,
          variant: 'warning',
        });
        return true;
      }

      toast({
        description: result.error.message,
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsAdding(false);
    }
  };

  const handleDeleteNote = async (noteID: number) => {
    if (!load.ID || deletingNoteID !== undefined) {
      return;
    }

    setDeletingNoteID(noteID);
    try {
      const result = await deleteGlobalTranzNote(load.ID, noteID);

      if (result.isOk()) {
        setHistory(result.value);
        setPendingDeleteNoteID(undefined);
        await onNotesChanged?.(result.value);
        toast({
          description: 'Successfully deleted note.',
          variant: 'success',
        });
        return;
      }

      if (result.error.level === 'warning') {
        // GTZ confirmed the delete but the refresh failed. Optimistically remove
        // the note from local state so the form payload stays consistent with GTZ.
        const withoutDeleted = history.filter((n) => n.noteId !== noteID);
        setHistory(withoutDeleted);
        setPendingDeleteNoteID(undefined);
        await onNotesChanged?.(withoutDeleted);
      }
      toast({
        description: result.error.message,
        variant: result.error.level === 'warning' ? 'warning' : 'destructive',
      });
    } finally {
      setDeletingNoteID(undefined);
    }
  };

  return (
    <Flex direction='col' gap='md' className='w-full min-w-0 px-3 pt-1'>
      <Flex align='center' justify='between' gap='sm' className='w-full'>
        <Typography
          variant='subtitle2'
          weight='semibold'
          className='text-neutral-800'
        >
          Note history
        </Typography>
        {!isCancelled && (
          <Button
            buttonNamePosthog={null}
            type='button'
            variant='secondary'
            size='sm'
            className='h-8 px-3 text-xs'
            onClick={() => handleAddDialogOpenChange(true)}
            logProperties={{
              loadID: load.ID,
              freightTrackingID: load.freightTrackingID,
              serviceID: load.serviceID,
            }}
          >
            <Plus className='mr-1 h-4 w-4' />
            Add Note
          </Button>
        )}
      </Flex>

      {!filteredNotes.length && (
        <Flex
          direction='col'
          align='center'
          gap='sm'
          className='w-full rounded-md border border-dashed border-neutral-300 px-3 py-6 text-center'
        >
          <Typography
            variant='body-sm'
            weight='medium'
            className='text-neutral-600'
          >
            No notes yet
          </Typography>
        </Flex>
      )}

      {filteredNotes.map((note) => (
        <Flex
          direction='col'
          gap='xs'
          key={note.noteId ?? `${note.userName}-${note.timeStamp}-${note.text}`}
          className='relative w-full min-w-0 max-w-full rounded-md border border-neutral-300 bg-neutral-50 p-3'
        >
          <Flex className='w-full min-w-0 pr-9'>
            {(note.userName || note.timeStamp) && (
              <Typography
                variant='body-xs'
                weight='medium'
                className='min-w-0 text-neutral-600'
              >
                {note.userName}
                {note.userName && note.timeStamp ? ' · ' : ''}
                {note.timeStamp
                  ? dayjs(note.timeStamp).format('MMM D, YYYY [at] h:mm A')
                  : ''}
              </Typography>
            )}
          </Flex>

          {note.canDeleteNote && note.noteId && !isCancelled && (
            <Button
              buttonNamePosthog={null}
              type='button'
              variant='ghost'
              size='xs'
              aria-label='Delete note'
              className='absolute right-3 top-3 text-neutral-400 hover:!border-error-200 hover:!bg-error-50 hover:!text-error-600 [&>svg]:stroke-current'
              disabled={deletingNoteID !== undefined}
              onClick={() => setPendingDeleteNoteID(note.noteId)}
            >
              <Trash2 className='h-4 w-4' />
            </Button>
          )}

          {((note.noteTypeID && noteTypeLabels.get(note.noteTypeID)) ||
            note.noteType ||
            note.isResolved) && (
            <Flex align='center' gap='sm' className='w-full flex-wrap'>
              {((note.noteTypeID && noteTypeLabels.get(note.noteTypeID)) ||
                note.noteType) && (
                <Typography
                  variant='body-xs'
                  weight='medium'
                  className='inline-flex items-center rounded px-2 py-0.5 bg-info-100 text-info-700 border border-info-200'
                >
                  {(note.noteTypeID && noteTypeLabels.get(note.noteTypeID)) ||
                    note.noteType}
                </Typography>
              )}

              {note.isResolved && (
                <Typography
                  variant='body-xs'
                  weight='medium'
                  className='inline-flex items-center px-2 py-0.5 rounded bg-success-50 text-success-700 border border-success-200'
                >
                  ✓ Resolved
                </Typography>
              )}
            </Flex>
          )}

          <NoteDescription text={note.text} />
        </Flex>
      ))}

      <AddNoteDialog
        open={isAddDialogOpen}
        onOpenChange={handleAddDialogOpenChange}
        noteTypes={noteTypes ?? []}
        noteTypesAreLoading={noteTypesAreLoading}
        noteTypesError={Boolean(noteTypesError)}
        isSaving={isAdding}
        onSave={handleAddNote}
        loadID={load.ID ?? undefined}
      />

      <DeleteNoteDialog
        open={pendingDeleteNoteID !== undefined}
        onOpenChange={(open) => {
          if (!open && deletingNoteID === undefined) {
            setPendingDeleteNoteID(undefined);
          }
        }}
        isDeleting={deletingNoteID !== undefined}
        onDelete={async () => {
          if (pendingDeleteNoteID !== undefined) {
            await handleDeleteNote(pendingDeleteNoteID);
          }
        }}
        loadID={load.ID ?? undefined}
      />
    </Flex>
  );
}
